import type { PartyType, VendorKind } from '@/lib/auth/session';

/**
 * 좌측 메뉴의 골격 — <b>여기가 유일한 정의</b>다. front 의 `menuConfig.ts` 와 같은 자리.
 *
 * <h2>묶음으로 접힌다</h2>
 * 메뉴가 열여덟이라 평면으로 쌓으면 목록이 된다. 「할 일인가, 기록인가, 찾아가서 보는 자료인가」로
 * 묶는다. 묶음의 이름과 경계는 <b>DOC-84 §7 ① 미결</b>이라 초안대로 두었다 — 사용자가 확정하면
 * 이 파일만 고친다.
 *
 * <h2>협력사와 감리는 갈 곳이 다르다</h2>
 * 계약·기성은 협력사만 — 감리에게는 남의 돈이라 메뉴에도 없다(DOC-82 결정 E). 감추는 것이 아니라
 * 서버가 주지 않는다. `parties` 가 그것을 적는다.
 *
 * <h2>직원은 업체가 없다 (DOC-87 §3-8)</h2>
 * 직원(`STAFF`)에게는 TBM·기성·계약·담당자가 없다 — 전부 업체의 것이다. 「기록」은 일보가 먼저다:
 * 직원의 기록은 작업일보 작성·제출이고(결정 ⑥), 출역·장비는 협력사가 적은 것을 보는 자리다.
 *
 * <h2>갈래로 한 번 더 거른다 (V348 · V364 · DOC-96 §9)</h2>
 * 협력사는 외주·구매·용역 중 <b>여럿</b>을 맡는다(업체의 속성 · V364 — 공사도 하고 자재도 대는 업체가
 * 있다). 구매만 하는 업체에게 TBM·출역·기성은 남의 일이고, 용역 업체에게 발주서·장비는 남의 일이다.
 * 항목의 `kinds` 와 업체의 갈래가 <b>하나라도 겹치면</b> 보인다. `kinds` 가 없는 항목은 전부 본다.
 *
 * <h2>★ 갈래가 <b>비면 막는다</b> (사용자 지시 2026-09-30 「지정이 안돼있으면 다 안돼야지」)</h2>
 * V348 부터 미지정은 「아직 안 정했다」로 읽어 <b>전부 보여 주었다</b>. 뒤집었다 — 권한은 <b>준 만큼</b>
 * 이어야 한다. 본사가 협력사 마스터에서 갈래를 켜 주기 전까지 그 업체는 갈래에 매인 메뉴를 못 본다.
 *
 * <p><b>감리·발주처도 같다</b>(사용자 지시 2026-09-30 「감리, 직원도 … 협력사 마스터에서 전부 관리」).
 * 그들도 협력사 마스터에 행이 있고(`vendor_master.party_type`) 계정을 거기서 연다 — 갈래를 줄 자리가
 * 있으니 예외로 둘 이유가 없다.
 *
 * <p><b>직원도 같다</b>(V369). 직원은 업체 표에 행이 없어 갈래를 담는 자리가 따로 있고
 * (`portal_staff_kinds`), 관리하는 화면은 같은 곳이다 — 협력사 마스터 › 직원 접속. 그래서 여기에
 * 예외가 하나도 없다: <b>부여받지 못한 사람은 못 들어온다.</b>
 *
 * <h2>키는 위젯 키와 같다</h2>
 * `key` 는 경로 마디이자 `pages/home/widgets/registry.ts` 의 `widgetKey` 다 — 위젯의 「전체보기」가
 * 가리키는 메뉴가 곧 이 항목이다. 한 화면의 탭을 가리키는 위젯(투입 인력)은 등록부가 `menuKey` 로
 * 그 항목을 적는다. 정합은 테스트가 지킨다.
 */

/**
 * 두 공간 (사용자 지시 2026-09-30 「페이지를 분리해서 가는거지」).
 *
 * <ul>
 *   <li>{@link WORK_SPACE} — 현장에서 하는 일. 관문의 갈래 칸 셋(외주·구매·용역)이 이 문이다.
 *   <li>{@link RFQ_SPACE} — 응찰하는 일. 관문의 「견적·입찰」 칸이 이 문이다.
 * </ul>
 */
export type Space = 'work' | 'rfq';

export const WORK_SPACE: Space = 'work';
export const RFQ_SPACE: Space = 'rfq';

/** 견적·입찰 공간의 주소 뿌리. 관문과 사이드바가 <b>같은 상수</b>를 본다. */
export const RFQ_SPACE_ROOT = '/rfqs';

/**
 * 이 경로는 어느 공간인가. <b>경로가 공간을 정한다</b> — 상태를 쥐지 않는다.
 *
 * <p>공고 상세(`/rfqs/:id`)도 견적 공간이다. 접두어로 보는 이유가 그것이다.
 */
export function spaceOfPath(pathname: string): Space {
  return pathname === RFQ_SPACE_ROOT || pathname.startsWith(RFQ_SPACE_ROOT + '/') ? RFQ_SPACE : WORK_SPACE;
}

export type MenuItem = {
  /** 경로 마디이자 위젯 키. */
  key: string;
  label: string;
  icon: string;
  /**
   * 검색에서만 걸리는 다른 이름.
   *
   * <p>현장에서 부르는 말이 화면 이름과 다르다 — 「기성 청구」를 「기성금」·「청구서」로,
   * 「부적합」을 「NCR」로 부른다. 이름을 정확히 아는 사람은 검색을 쓰지 않는다.
   */
  aliases?: string[];
  parties: PartyType[];
  /** 어느 갈래의 협력사가 보는가. 없으면 전부. 갈래를 모르는 업체·직원·감리에게는 적용되지 않는다. */
  kinds?: VendorKind[];
  /** 현장 아래(`/sites/:siteId/…`)인가, 업체의 것(`/members`)인가. */
  scope: 'site' | 'party';
  /**
   * 어느 <b>공간</b>의 메뉴인가 (사용자 지시 2026-09-30). 없으면 {@link WORK_SPACE}.
   *
   * <p>이 앱은 두 일을 한다 — 현장에서 <b>검측·작업일보를 쓰는 일</b>과 <b>응찰하는 일</b>이다.
   * 둘은 사람도 때도 다르다: 앞엣것은 현장 담당자가 매일, 뒤엣것은 견적 담당자가 공고를 받을 때다.
   * 한 사이드바에 섞어 두면 매일 쓰는 목록에 어쩌다 쓰는 줄이 끼고, 그 반대도 된다.
   *
   * <p>공간은 <b>경로가 정한다</b>({@link spaceOfPath}) — 상태로 들고 있지 않다. 들고 있으면
   * 새로고침·뒤로가기·링크로 들어온 사람의 사이드바가 화면과 어긋난다.
   */
  space?: Space;
  /**
   * 묶음 안의 순서가 주체마다 다를 때만 적는다. 없으면 적힌 순서다.
   * 「기록」에서 감리는 감리일지가 먼저고 협력사는 출역이 먼저인 것이 그 예다.
   */
  order?: Partial<Record<PartyType, number>>;
};

export type MenuGroup = {
  key: string;
  label: string;
  items: MenuItem[];
};

const EVERYONE: PartyType[] = ['VENDOR', 'SUPERVISOR', 'OWNER', 'STAFF'];
const VENDOR_ONLY: PartyType[] = ['VENDOR'];
/** 응찰은 협력사만, 보기는 직원도 — 견적·입찰 한 줄이 쓴다(V370). */
const VENDOR_AND_STAFF: PartyType[] = ['VENDOR', 'STAFF'];
/** 업체에 매인 것 — 직원은 업체가 없어 여기 없다. */
const EXTERNAL_ONLY: PartyType[] = ['VENDOR', 'SUPERVISOR', 'OWNER'];

/** 현장에 사람과 장비를 넣는 것 — 외주만. 구매·용역 업체는 TBM 에 서명할 사람이 현장에 없다. */
const SITE_WORK: VendorKind[] = ['SUBCONTRACT'];
/** 공사의 진행에 매인 것 — 외주와 용역. 구매 업체는 물건을 대고 끝난다. */
const CONSTRUCTION: VendorKind[] = ['SUBCONTRACT', 'SERVICE'];
/** 물건이 오가는 것 — 외주와 구매. */
const GOODS: VendorKind[] = ['SUBCONTRACT', 'PURCHASE'];

export const MENU_GROUPS: MenuGroup[] = [
  {
    // 할 일이 아니라 상태. 둘 다 같은 것을 본다(경계가 site_id 하나다).
    key: 'site',
    label: '현장',
    items: [
      { key: 'overview', label: '현장 개요', icon: 'info', aliases: ['현장정보', '발주처', '공사기간'], parties: EVERYONE, scope: 'site' },
      { key: 'progress', label: '공정', icon: 'trending', aliases: ['공정율', '진척', 'wbs'], parties: EVERYONE, kinds: CONSTRUCTION, scope: 'site' },
      { key: 'contacts', label: '연락처', icon: 'phone', aliases: ['담당자', '전화', '소장'], parties: EVERYONE, scope: 'site' },
    ],
  },
  {
    // 답해야 닫히는 것. 건수 배지가 붙는 자리다(`useTodoCounts`). 이름은 사용자가 정했다(2026-09-10 · §7 ①).
    key: 'today',
    label: '오늘 할 일',
    items: [
      { key: 'safety', label: '안전', icon: 'lock', aliases: ['tbm', '안전교육', '서명'], parties: VENDOR_ONLY, kinds: SITE_WORK, scope: 'site' },
      { key: 'inspections', label: '검측', icon: 'check', aliases: ['검사', '입회', '판정'], parties: EVERYONE, kinds: CONSTRUCTION, scope: 'site' },
      { key: 'safety-inspections', label: '안전 점검', icon: 'alert', aliases: ['지적', '시정조치'], parties: EVERYONE, kinds: SITE_WORK, scope: 'site' },
      { key: 'ncrs', label: '부적합', icon: 'flag', aliases: ['ncr', '하자', '재시공'], parties: EVERYONE, kinds: CONSTRUCTION, scope: 'site' },
    ],
  },
  {
    // 한쪽이 내고 다른 쪽이 닫는 것.
    key: 'submit',
    label: '제출·승인',
    items: [
      /*
        견적·입찰(DOC-98 §5-1). 셋 중 **일의 순서로 맨 앞**이다 — 견적을 내야 계약이 서고 기성이 붙는다.

        · `scope: 'party'` — 경로에 현장이 없다(`/rfqs`). 공고가 현장을 들고 있고 협력사는 여러 현장 것을
          한 목록에서 본다. 마감 임박한 것을 현장별로 나눠 보게 하면 놓친다.
        · `VENDOR_AND_STAFF` — 응찰은 협력사만 하고, <b>직원은 보기만</b> 한다(V370 · 사용자 지시
          2026-09-30 「직원도 포털에서 공고를 보게」). 직원에게 열리는 것은 배정 현장의 공고까지이고,
          응찰·참가의사·철회는 서버가 그대로 막는다. 감리·발주처는 아직 이 자리가 없다.
        · 그림은 자물쇠다. 입찰은 마감까지 금액이 봉인된다(결정 ②) — 이 묶음에서 안 쓰던 그림이기도 하다.

        ★ `kinds` 가 **없다** — 갈래를 가리지 않는다(사용자 확정 2026-09-30).

          처음에는 `CONSTRUCTION`(외주·용역)이었다(DOC-98 결정 ⑩ 「구매 업체에게 공종 견적요청은 남의
          일이다」). 걷었다 — **자재도 견적을 받는다.** 철근 420톤 단가를 묻는 상대가 바로 구매 업체다.
          메뉴가 없으면 본사가 그 업체를 초청해도 상대 화면에 아무것도 도착하지 않는다.

          갈래로 거를 이유가 애초에 약했다: 어차피 보이는 것은 <b>초청받은 공고</b>뿐이다(지명경쟁 ·
          결정 ⑦). 공종 하도급을 구매 업체에게 물 일이 없으므로 그 업체의 목록에 그 공고가 설 일도 없다.
      */
      { key: 'rfqs', label: '견적·입찰', icon: 'lock', aliases: ['견적', '입찰', '응찰', '공고'], parties: VENDOR_AND_STAFF, scope: 'party', space: RFQ_SPACE },
      { key: 'drawings', label: '시공도·자재', icon: 'grid', aliases: ['shop', '샵도면', '자재승인'], parties: EVERYONE, scope: 'site' },
      { key: 'test-reports', label: '시험성적서', icon: 'flask', aliases: ['품질시험', '성적서'], parties: EVERYONE, scope: 'site' },
      { key: 'purchase', label: '구매·납품', icon: 'truck', aliases: ['발주', 'po', '납품'], parties: EVERYONE, kinds: GOODS, scope: 'site' },
      { key: 'claims', label: '기성 청구', icon: 'chart', aliases: ['기성금', '청구서', '대금'], parties: VENDOR_ONLY, kinds: CONSTRUCTION, scope: 'site' },
    ],
  },
  {
    // 매일 쌓이는 것.
    key: 'records',
    label: '기록',
    items: [
      // 출역과 투입 인력은 한 화면의 두 탭이다(사용자 결정 2026-09-10 · §7 ②). 위젯 `workers` 는 이 항목의 둘째 탭을 가리킨다.
      { key: 'labor', label: '출역·투입 인력', icon: 'users', aliases: ['출력', '노무', '인원', '근로자', '명단', '투입인력'], parties: EVERYONE, kinds: SITE_WORK, scope: 'site', order: { SUPERVISOR: 2, STAFF: 2 } },
      { key: 'equipment', label: '장비 가동', icon: 'wrench', aliases: ['중장비', '가동일보'], parties: EVERYONE, kinds: SITE_WORK, scope: 'site', order: { SUPERVISOR: 3, STAFF: 3 } },
      { key: 'daily', label: '작업일보', icon: 'clipboard', aliases: ['일일보고', '작업일지'], parties: EVERYONE, kinds: CONSTRUCTION, scope: 'site', order: { SUPERVISOR: 4, STAFF: 1 } },
      { key: 'supervision', label: '감리일지', icon: 'clipboard', aliases: ['감리'], parties: EVERYONE, kinds: CONSTRUCTION, scope: 'site', order: { SUPERVISOR: 1, STAFF: 4 } },
    ],
  },
  {
    // 찾아가서 보는 것.
    key: 'files',
    label: '자료',
    items: [
      { key: 'contract', label: '계약', icon: 'layout', aliases: ['하도급', '계약금액', '변경계약'], parties: VENDOR_ONLY, kinds: CONSTRUCTION, scope: 'site' },
      { key: 'documents', label: '문서', icon: 'news', aliases: ['자료', '첨부', '다운로드'], parties: EVERYONE, scope: 'site' },
    ],
  },
  {
    // 현장 밖.
    key: 'party',
    label: '업체',
    items: [
      { key: 'members', label: '담당자', icon: 'users', aliases: ['직원', '계정', '초대'], parties: EXTERNAL_ONLY, scope: 'party' },
    ],
  },
];

/**
 * 이 주체가 갈 수 있는 묶음과 항목. 빈 묶음은 빠진다.
 *
 * @param kinds 업체가 맡는 갈래들. 협력사인데 <b>비어 있으면 갈래에 매인 항목이 전부 빠진다</b>
 * @param space 지금 서 있는 공간. 그 공간의 항목만 남는다 — 기본은 현장 업무다
 */
export function menuFor(
  partyType: PartyType,
  kinds: VendorKind[] = [],
  space: Space = WORK_SPACE,
): MenuGroup[] {
  return MENU_GROUPS.map((group) => ({
    ...group,
    items: group.items
      .map((item, index) => ({ item, rank: item.order?.[partyType] ?? index }))
      .filter(({ item }) => item.parties.includes(partyType))
      /*
        하나라도 겹치면 보인다. 갈래가 비어 있으면 갈래에 매인 항목은 <b>전부 빠진다</b> —
        협력사·감리·발주처·직원이 모두 같다(위 ★). 예외를 두지 않는 것이 이 규칙의 전부다.
      */
      .filter(({ item }) => item.kinds === undefined
        || item.kinds.some((wanted) => kinds.includes(wanted)))
      // 공간이 적히지 않은 항목은 현장 업무다 — 줄마다 적게 하면 새 항목에서 빠뜨린다.
      .filter(({ item }) => (item.space ?? WORK_SPACE) === space)
      .sort((a, b) => a.rank - b.rank)
      .map(({ item }) => item),
  })).filter((group) => group.items.length > 0);
}

/** 항목이 가리키는 주소. 현장 항목은 현장이 정해져야 갈 곳이 정해진다. */
export function hrefOf(item: MenuItem, siteId: string | undefined): string | null {
  if (item.scope === 'party') return `/${item.key}`;
  return siteId === undefined ? null : `/sites/${siteId}/${item.key}`;
}

/** 위젯 「전체보기」가 가리키는 메뉴 키 전부 — 등록부와의 정합 테스트가 쓴다. */
export function menuKeys(): Set<string> {
  return new Set(MENU_GROUPS.flatMap((group) => group.items.map((item) => item.key)));
}
